/**
 * Loading Komponenten für CherryPulse
 */

import React from 'react';

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const spinnerSizes = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

export function Spinner({ size = 'md', className = '' }: SpinnerProps): React.ReactElement {
  return (
    <svg
      className={`animate-spin text-cherry-600 ${spinnerSizes[size]} ${className}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

interface LoadingScreenProps {
  message?: string;
}

export function LoadingScreen({ message = 'Laden...' }: LoadingScreenProps): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
      <Spinner size="lg" />
      <p className="text-charcoal-500">{message}</p>
    </div>
  );
}

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className = '' }: SkeletonProps): React.ReactElement {
  return (
    <div
      className={`animate-pulse bg-charcoal-100 rounded ${className}`}
      aria-hidden="true"
    />
  );
}

// Skeleton für Agency Cards
export function AgencyCardSkeleton(): JSX.Element {
  return (
    <div className="bg-white rounded-2xl p-6 border border-charcoal-100">
      <div className="flex items-start gap-4">
        <Skeleton className="w-14 h-14 rounded-xl" />
        <div className="flex-1">
          <Skeleton className="h-5 w-36 mb-2" />
          <Skeleton className="h-4 w-28 mb-1" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="w-12 h-12 rounded-xl" />
      </div>
    </div>
  );
}

// Skeleton für Dashboard Stats
export function StatCardSkeleton(): JSX.Element {
  return (
    <div className="bg-white rounded-2xl p-6 border border-charcoal-100">
      <Skeleton className="h-4 w-20 mb-3" />
      <Skeleton className="h-8 w-16 mb-2" />
      <Skeleton className="h-3 w-24" />
    </div>
  );
}

// Skeleton für Rating Timeline
export function TimelineSkeleton(): JSX.Element {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="w-10 h-10 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-4 w-32 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="w-12 h-6 rounded" />
        </div>
      ))}
    </div>
  );
}

// Skeleton für Charts
export function ChartSkeleton(): JSX.Element {
  return (
    <div className="bg-charcoal-50 rounded-xl p-4">
      <Skeleton className="h-48 w-full rounded-lg" />
    </div>
  );
}

// EmptyState Komponente
interface EmptyStateProps {
  icon?: string;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({
  icon = '📭',
  title,
  description,
  action,
  className = '',
}: EmptyStateProps): JSX.Element {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}>
      <span className="text-5xl mb-4" role="img" aria-hidden="true">
        {icon}
      </span>
      <h3 className="text-lg font-semibold text-charcoal-800 mb-2">{title}</h3>
      {description && (
        <p className="text-charcoal-500 max-w-sm mb-6">{description}</p>
      )}
      {action && (
        <button
          onClick={action.onClick}
          className="px-6 py-3 bg-cherry-600 text-white rounded-xl font-semibold
                     hover:bg-cherry-700 transition-colors focus:outline-none
                     focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

// ErrorState Komponente mit Retry
interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  title = 'Etwas ist schiefgelaufen',
  message,
  onRetry,
  className = '',
}: ErrorStateProps): JSX.Element {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}>
      <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-charcoal-800 mb-2">{title}</h3>
      <p className="text-charcoal-500 max-w-sm mb-6">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-6 py-3 bg-charcoal-800 text-white rounded-xl font-semibold
                     hover:bg-charcoal-900 transition-colors focus:outline-none
                     focus:ring-2 focus:ring-charcoal-600 focus:ring-offset-2
                     inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Erneut versuchen
        </button>
      )}
    </div>
  );
}
